import React from 'react';
import {
  Card,
  makeStyles,
  tokens,
  Body1,
  Skeleton,
  SkeletonItem
} from '@fluentui/react-components';
import {
  Building20Regular,
  HatGraduation20Regular,
  PersonAccounts20Regular,
  PeopleTeam20Regular
} from '@fluentui/react-icons';
import type { DashboardSummary } from '../../types';
import { dashboardService } from '../../services/dashboardService';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '16px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  icon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconSchools: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: tokens.colorPalettePurpleForeground2,
  },
  iconStudents: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  iconTeachers: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  iconClasses: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 0,
  },
  value: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1.2,
    textAlign: 'center' as const,
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textAlign: 'center' as const,
  },
  skeletonCard: {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
});

interface SummaryCardsProps {
  summary: DashboardSummary | null;
  loading?: boolean;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary, loading }) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.container}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className={styles.skeletonCard}>
            <Skeleton>
              <SkeletonItem shape="square" size={36} />
            </Skeleton>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <Skeleton><SkeletonItem size={24} style={{ width: '60px' }} /></Skeleton>
              <Skeleton><SkeletonItem size={12} style={{ width: '50px' }} /></Skeleton>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      icon: <Building20Regular />,
      iconClass: styles.iconSchools,
      value: summary?.totalSchools ?? 0,
      label: 'Escolas',
    },
    {
      icon: <HatGraduation20Regular />,
      iconClass: styles.iconStudents,
      value: summary?.totalStudents ?? 0,
      label: 'Alunos',
    },
    {
      icon: <PersonAccounts20Regular />,
      iconClass: styles.iconTeachers,
      value: summary?.totalTeachers ?? 0,
      label: 'Professores',
    },
    {
      icon: <PeopleTeam20Regular />,
      iconClass: styles.iconClasses,
      value: summary?.totalClasses ?? 0,
      label: 'Classes',
    },
  ];

  return (
    <div className={styles.container}>
      {cards.map((card, index) => (
        <Card key={index} className={styles.card}>
          <div className={`${styles.icon} ${card.iconClass}`}>
            {card.icon}
          </div>
          <div className={styles.content}>
            <div className={styles.value}>
              {dashboardService.formatNumber(card.value)}
            </div>
            <Body1 className={styles.label}>{card.label}</Body1>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default SummaryCards;
